'use client'

import { useState, useEffect, useRef, useCallback } from 'react'

interface AddressSearchProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
}

interface AddressResult {
  sla: string
}

export function AddressSearch({
  value,
  onChange,
  placeholder = 'Type your address',
}: AddressSearchProps) {
  const [inputValue, setInputValue] = useState(value)
  const [results, setResults] = useState<AddressResult[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const search = useCallback(async (q: string) => {
    if (q.length < 3) {
      setResults([])
      setIsOpen(false)
      return
    }
    try {
      const res = await fetch(`/api/address-lookup?q=${encodeURIComponent(q)}`)
      const data: AddressResult[] = await res.json()
      setResults(data.slice(0, 8))
      setIsOpen(data.length > 0)
      setHighlightIndex(-1)
    } catch {
      setResults([])
    }
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const q = e.target.value
    setInputValue(q)
    onChange(q)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(q), 250)
  }

  const selectAddress = useCallback(
    (address: string) => {
      setInputValue(address)
      onChange(address)
      setIsOpen(false)
      setResults([])
    },
    [onChange],
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && results[highlightIndex]) {
        selectAddress(results[highlightIndex].sla)
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  useEffect(() => {
    setInputValue(value)
  }, [value])

  return (
    <div ref={wrapperRef} className="relative w-full">
      {/* Figma Trigger 4022:82014: bg theme/muted #e0dfe0, NO border, radius sm 2px,
          h-9 (36px), px padding/p-3 12, py padding/p-2 8, effect shadow/xs
          (0 1px 2px rgba(0,0,0,0.05)); Body/bodySm Lato light 14/16;
          focus ring theme/ring at spread 3 */}
      <div
        className="flex items-center bg-muted rounded-sm shadow-xs h-9 px-3 cursor-text focus-within:ring-[3px] focus-within:ring-ring focus-within:ring-offset-0"
        onClick={() => inputRef.current?.focus()}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true)
          }}
          placeholder={placeholder}
          className="flex-1 min-w-0 bg-transparent text-body-sm font-light text-foreground placeholder:text-muted-foreground focus:outline-none"
          autoComplete="off"
          aria-label="Property address"
          // combobox role: aria-expanded/aria-autocomplete are only valid on a
          // combobox, not a bare textbox (a11y audit: "unsupported ARIA attribute")
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls="address-search-listbox"
        />
        {/* Search icon */}
        <svg
          className="shrink-0 text-brand/40 ml-2"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </div>

      {isOpen && results.length > 0 && (
        <ul
          id="address-search-listbox"
          className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border shadow-md max-h-64 overflow-y-auto rounded-sm"
          role="listbox"
        >
          {results.map((result, i) => (
            <li
              key={result.sla}
              role="option"
              aria-selected={i === highlightIndex}
              onMouseDown={(e) => {
                e.preventDefault()
                selectAddress(result.sla)
              }}
              onMouseEnter={() => setHighlightIndex(i)}
              className={`px-4 py-2.5 text-body-sm cursor-pointer ${
                i === highlightIndex
                  ? // Figma menu item 2198:36854 highlighted state: theme/secondary fill, theme/foreground text
                    'bg-secondary text-foreground'
                  : 'text-brand hover:bg-secondary'
              }`}
            >
              {result.sla}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
